/**
 * Durable edit buffer for git-backed document editing (ADR-033, git-native
 * editing — "never lose an edit").
 *
 * When you edit a repo document in the reader, every change is written here
 * (IndexedDB) as well as held in React state, so an unsaved edit survives a
 * tab close, a crash, or navigating away before it is committed to GitHub.
 * On the next open of the same file the reader offers to restore it. A buffer
 * is cleared once its edit is committed. Keyed by repo + path + branch, and
 * carrying the blob sha the edit was based on so the commit can be conditional.
 *
 * Mirrors `indexedDBDraftsService` — a thin, resilient store; the decision
 * logic (what is restorable) lives in `lib/editBuffer.ts` so it is unit-tested.
 */

import { createLogger } from '@/lib/logger';

const log = createLogger('editBufferService');

const DB_NAME = 'systemdox-edit-buffers';
const DB_VERSION = 1;
const STORE_NAME = 'buffers';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export interface EditBuffer {
  /** `${repoFullName}::${path}::${branch}` — see `editBufferKey`. */
  key: string;
  repoFullName: string;
  path: string;
  /** '' for the repo's default branch. */
  branch: string;
  /** The unsaved edited content. */
  content: string;
  /** The blob sha the edit was based on (optimistic-concurrency base on commit). */
  baseSha: string;
  updatedAt: number;
}

class EditBufferService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private isClosing = false;

  private available(): boolean {
    return this.db !== null && !this.isClosing;
  }

  private async init(): Promise<void> {
    if (this.db && !this.isClosing) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onerror = () => {
        log.error('Failed to open edit-buffer IndexedDB', request.error as unknown as Error);
        reject(new Error('Failed to open edit-buffer IndexedDB'));
      };
      request.onsuccess = () => {
        this.db = request.result;
        this.isClosing = false;
        this.db.onclose = () => { this.isClosing = true; };
        resolve();
      };
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
        }
      };
    });
    return this.initPromise;
  }

  async save(buffer: EditBuffer): Promise<void> {
    try {
      await this.init();
      if (!this.available()) return;
      const tx = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      await new Promise<void>((resolve, reject) => {
        const req = store.put(buffer);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch (err) {
      // A failed local write must never break editing — the edit is still in
      // React state; we just lose the crash-durability for this keystroke.
      log.error('Failed to save edit buffer', err as Error);
    }
  }

  async get(key: string): Promise<EditBuffer | null> {
    try {
      await this.init();
      if (!this.available()) return null;
      const tx = this.db!.transaction([STORE_NAME], 'readonly');
      const store = tx.objectStore(STORE_NAME);
      return await new Promise((resolve, reject) => {
        const req = store.get(key);
        req.onsuccess = () => resolve((req.result as EditBuffer) || null);
        req.onerror = () => reject(req.error);
      });
    } catch (err) {
      log.error('Failed to read edit buffer', err as Error);
      return null;
    }
  }

  async remove(key: string): Promise<void> {
    try {
      await this.init();
      if (!this.available()) return;
      const tx = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      await new Promise<void>((resolve, reject) => {
        const req = store.delete(key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch (err) {
      log.error('Failed to delete edit buffer', err as Error);
    }
  }

  /** Best-effort reap of buffers older than MAX_AGE_MS. */
  async pruneStale(): Promise<void> {
    try {
      await this.init();
      if (!this.available()) return;
      const tx = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const all = await new Promise<EditBuffer[]>((resolve, reject) => {
        const req = store.getAll();
        req.onsuccess = () => resolve((req.result as EditBuffer[]) || []);
        req.onerror = () => reject(req.error);
      });
      const cutoff = Date.now() - MAX_AGE_MS;
      for (const b of all) {
        if (b.updatedAt < cutoff) store.delete(b.key);
      }
    } catch (err) {
      log.error('Failed to prune edit buffers', err as Error);
    }
  }
}

export const editBufferService = new EditBufferService();
